import React from 'react';
import { Download, User, Heart, Award } from 'lucide-react';
import Carousel from './Carousel';

const HowItWorks: React.FC = () => {
  const steps = [
    {
      icon: Download,
      title: 'Baixe o App',
      description: 'Faça o download gratuito do SantoGram na App Store ou Google Play.',
      color: 'from-gold-500 to-gold-600'
    },
    {
      icon: User,
      title: 'Crie seu Perfil',
      description: 'Configure suas preferências de oração e escolha seus santos favoritos.',
      color: 'from-gold-600 to-brown-500'
    },
    {
      icon: Heart,
      title: 'Inicie sua Jornada',
      description: 'Receba conteúdo diário personalizado e comece a crescer espiritualmente.',
      color: 'from-brown-500 to-brown-600'
    },
    {
      icon: Award,
      title: 'Cresça na Santidade',
      description: 'Acompanhe seu progresso e seja inspirado pelos santos todos os dias.',
      color: 'from-brown-600 to-gold-500'
    }
  ];

  return (
    <section id="como-funciona" className="py-20 bg-gradient-to-br from-cream-50 to-gold-50">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 bg-brown-100 text-brown-800 rounded-full text-sm font-semibold mb-4">
            🚀 Como Funciona
          </span>
          <h2 className="text-4xl md:text-5xl font-display font-bold text-brown-800 mb-6">
            Simples como uma{' '}
            <span className="text-gradient">oração</span>
          </h2>
          <p className="text-xl text-brown-600 max-w-3xl mx-auto">
            Em apenas alguns passos, você terá acesso a uma experiência espiritual 
            completa e transformadora.
          </p>
        </div>

        {/* Steps - Desktop */}
        <div className="relative hidden md:block">
          {/* Connection Line - Hidden on mobile */}
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-gold-300 via-brown-300 to-gold-300 transform -translate-y-1/2 z-0"></div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 relative z-10">
            {steps.map((step, index) => (
              <div key={index} className="text-center group">
                {/* Step Number */}
                <div className="relative mb-6">
                  <div className={`w-20 h-20 bg-gradient-to-r ${step.color} rounded-full flex items-center justify-center mx-auto shadow-xl group-hover:scale-110 transition-transform duration-300`}>
                    <step.icon className="w-10 h-10 text-white" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg border-2 border-cream-200">
                    <span className="text-sm font-bold text-brown-800">{index + 1}</span>
                  </div>
                </div>

                {/* Content */}
                <div className="bg-white rounded-xl p-6 shadow-lg border border-cream-200 group-hover:shadow-xl transition-shadow duration-300">
                  <h3 className="text-xl font-display font-bold text-brown-800 mb-3">
                    {step.title}
                  </h3>
                  <p className="text-brown-600 leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Steps Carousel - Mobile */}
        <div className="md:hidden">
          <Carousel autoPlayInterval={5000} showArrows={true} showDots={true}>
            {steps.map((step, index) => (
              <div key={index} className="text-center group">
                {/* Step Number */}
                <div className="relative mb-6">
                  <div className={`w-20 h-20 bg-gradient-to-r ${step.color} rounded-full flex items-center justify-center mx-auto shadow-xl group-hover:scale-110 transition-transform duration-300`}>
                    <step.icon className="w-10 h-10 text-white" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg border-2 border-cream-200">
                    <span className="text-sm font-bold text-brown-800">{index + 1}</span>
                  </div>
                </div>

                {/* Content */}
                <div className="bg-white rounded-xl p-6 shadow-lg border border-cream-200 group-hover:shadow-xl transition-shadow duration-300">
                  <h3 className="text-xl font-display font-bold text-brown-800 mb-3">
                    {step.title}
                  </h3>
                  <p className="text-brown-600 leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </Carousel>
        </div>

        {/* Bottom Section */}
        <div className="mt-20 text-center">
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-xl border border-cream-200 max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              {/* Left Content */}
              <div className="text-left">
                <h3 className="text-3xl font-display font-bold text-brown-800 mb-4">
                  Nosso propósito com o SantoGram
                </h3>
                <p className="text-brown-600 mb-6 leading-relaxed">
                  Nosso propósito é ajudar você a se conectar com Deus de uma forma mais profunda e significativa.
                </p>
                
                {/* Stats */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-cream-50 rounded-lg">
                    <div className="text-2xl font-bold text-gold-600">50k+</div>
                    <div className="text-sm text-brown-600">Meta de usuários</div>
                  </div>
                  <div className="text-center p-4 bg-cream-50 rounded-lg">
                    <div className="text-2xl font-bold text-gold-600">1M+</div>
                    <div className="text-sm text-brown-600">Meta orações rezadas</div>
                  </div>
                </div>
              </div>

              {/* Right Content */}
              <div className="relative">
                <div className="bg-gradient-to-br from-gold-100 to-brown-100 rounded-xl p-8 relative overflow-hidden">
                  <div className="absolute top-4 right-4 text-4xl opacity-20">✝</div>
                  <div className="absolute bottom-4 left-4 text-3xl opacity-20">🙏</div>
                  
                  <div className="text-center relative z-10">
                    <div className="w-20 h-20 md:w-24 md:h-24 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg border-2 border-cream-200">
                      <img 
                        src="/santogram_logo.png" 
                        alt="SantoGram Logo" 
                        className="w-12 h-12 md:w-14 md:h-14 object-contain"
                      />
                    </div>
                    <h4 className="font-display font-bold text-brown-800 mb-2">
                      Comece hoje mesmo
                    </h4>
                    <p className="text-brown-600 text-sm mb-4">
                      Sua jornada espiritual está a um clique de distância
                    </p>
                    <a href="#inscrever" className="btn-primary w-full inline-block text-center">
                      Inscreva-se Agora
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks; 